/**
 * Embed API - Type-Safe Public Endpoints
 *
 * Standalone API functions for embed chat initialization.
 * Uses Zod validation from ZDeployments for end-to-end type safety.
 *
 * These functions are designed to work outside React (Redux thunks)
 * and don't require authentication (public endpoints).
 *
 * @example
 * ```typescript
 * // In Redux thunk
 * const embedInfo = await embedApi.getPublicInfo({ deploymentId: "..." })
 * const chatroom = await embedApi.getChatroom({ deploymentId: "...", sessionId: "..." })
 * ```
 */

import { type DbEnum, type DeploymentResponse, type TApi } from "@nexus/types"
import { ZDeployments } from "@nexus/types/contracts"

import { toFetchHeaders } from "./chat-request-headers"
import { getBackendUrl } from "./config"
import { fetchEmbedPublicInfo } from "./embed-bootstrap"

// =============================================================================
// TYPE ALIASES
// =============================================================================

/** Response for GET /api/deployments/embed/:id/public (same as GetDeployment) */
export type EmbedPublicInfo = DeploymentResponse

/** Response type for GET /api/deployments/:id/chatroom */
export type DeploymentChatroom = TApi["Deployments"]["GetDeploymentChatroom"]["Response"]["data"]

/** Response type for GET /api/deployments/:id/embed-sessions */
export type EmbedSessionsResponse = TApi["Deployments"]["GetEmbedSessions"]["Response"]["data"]

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get public embed deployment info
 *
 * PUBLIC endpoint - no authentication required.
 * Used by embed widgets to fetch their configuration.
 *
 * The resilience and the failure taxonomy live in `embed-bootstrap.ts`: this
 * is the ONE request the whole widget depends on, so it carries a timeout and
 * a bounded retry, and it fails with an `EmbedBootstrapError` that names its
 * cause rather than with the browser's untriageable "Load failed".
 *
 * @param params.deploymentId - The deployment UUID
 * @param params.signal - Optional caller signal; aborting it stops the retries
 * @returns DeploymentResponse - Validated (same shape as GetDeployment)
 * @throws EmbedBootstrapError naming the reason it gave up
 */
export async function getEmbedPublicInfo({
  deploymentId,
  signal
}: {
  deploymentId: string
  signal?: AbortSignal
}): Promise<DeploymentResponse> {
  return fetchEmbedPublicInfo(deploymentId, signal)
}

/**
 * Get or create chatroom for a deployment session
 *
 * PUBLIC endpoint - no authentication required.
 * Returns existing chatroom or reserves a new UUID for first message.
 *
 * @param params.deploymentId - The deployment UUID
 * @param params.sessionId - The session UUID (from sessionStorage)
 * @returns DeploymentChatroom - Validated response data
 * @throws Error if request fails or validation fails
 */
export async function getDeploymentChatroom({
  deploymentId,
  sessionId
}: {
  deploymentId: string
  sessionId: string
}): Promise<DeploymentChatroom> {
  const path = ZDeployments.GetDeploymentChatroom.path.replace(":id", deploymentId)

  // The session id rides in `x-session-id` and never in the URL: it is the
  // capability to resume this conversation, and a query string lands in access
  // logs and in `Referer`. The backend reads the header alone.
  const response = await fetch(`${getBackendUrl()}${path}`, {
    method: ZDeployments.GetDeploymentChatroom.method,
    headers: toFetchHeaders({
      "Content-Type": "application/json",
      "x-session-id": sessionId
    })
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error")
    throw new Error(`Failed to get deployment chatroom: ${response.status} ${errorText}`)
  }

  // Parse and validate with Zod (unwraps { success, data } automatically)
  const json = await response.json()
  const validated = ZDeployments.GetDeploymentChatroom.Response.parse(json)

  return validated.data
}

/**
 * Get past embed conversation summaries
 *
 * PUBLIC endpoint - no authentication required.
 * Returns summaries of past conversations for given session IDs.
 *
 * @param params.deploymentId - The deployment UUID
 * @param params.sessionIds - Array of session UUIDs to look up
 * @returns EmbedSessionsResponse - Validated response data
 * @throws Error if request fails or validation fails
 */
export async function getEmbedSessions({
  deploymentId,
  sessionIds
}: {
  deploymentId: string
  sessionIds: string[]
}): Promise<EmbedSessionsResponse> {
  if (sessionIds.length === 0) {
    return { sessions: [] }
  }

  const path = ZDeployments.GetEmbedSessions.path.replace(":id", deploymentId)

  const response = await fetch(`${getBackendUrl()}${path}`, {
    method: ZDeployments.GetEmbedSessions.method,
    // The ids ride in `x-session-ids` and never in the URL: each one is the
    // capability to read a conversation, and a query string lands in access
    // logs and in `Referer`. That custom header makes this a preflighted
    // request, which is the accepted cost; the backend's CORS `allowedHeaders`
    // admits it. Still no `Content-Type` — a bodyless GET has no body to describe.
    headers: toFetchHeaders({ "x-session-ids": sessionIds.join(",") })
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error")
    throw new Error(`Failed to get embed sessions: ${response.status} ${errorText}`)
  }

  const json = await response.json()
  const validated = ZDeployments.GetEmbedSessions.Response.parse(json)

  return validated.data
}

// =============================================================================
// NAMESPACE EXPORT (for cleaner imports)
// =============================================================================

/**
 * Embed API namespace
 *
 * @example
 * ```typescript
 * import { embedApi } from "@/common/services/chat"
 *
 * const info = await embedApi.getPublicInfo({ deploymentId })
 * const chatroom = await embedApi.getChatroom({ deploymentId, sessionId })
 * ```
 */
interface CustomerEmbedSession {
  sessionId: string
  chatId: string
  topic: string | null
  lastMessagePreview: string | null
  lastMessageAt: string | null
  messageCount: number
  status?: DbEnum.ChatStatus
}

interface CustomerEmbedSessionsResponse {
  sessions: CustomerEmbedSession[]
  customer: { id: string; displayName: string | null; externalUserId: string | null } | null
}

/**
 * Fetch past sessions for a customer identified by externalUserId or customerId.
 * Used by the embed landing screen for cross-device conversation continuity.
 *
 * Public endpoint — no auth required.
 */
async function getCustomerEmbedSessions({
  deploymentId,
  externalUserId,
  customerId,
  userHash
}: {
  deploymentId: string
  externalUserId?: string
  customerId?: string
  /** HMAC user_hash, required when deployment has identity verification enabled */
  userHash?: string
}): Promise<CustomerEmbedSessionsResponse> {
  const response = await fetch(
    `${getBackendUrl()}/api/deployments/embed/${deploymentId}/customer-sessions`,
    {
      method: "GET",
      // The identity rides in headers and never in the URL: the `user_hash` is a
      // permanent HMAC over the visitor id with no timestamp or nonce, so one
      // written into an access log reads that customer's history for good. An
      // empty or absent value sends no header at all. No `Content-Type` on a
      // bodyless GET — see `getEmbedSessions` above.
      headers: toFetchHeaders({
        "x-external-user-id": externalUserId || undefined,
        "x-customer-id": customerId || undefined,
        "x-user-hash": userHash || undefined
      })
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch customer sessions: ${response.statusText}`)
  }

  const result = (await response.json()) as {
    success: boolean
    data: CustomerEmbedSessionsResponse
  }
  return result.data
}

export const embedApi = {
  getPublicInfo: getEmbedPublicInfo,
  getChatroom: getDeploymentChatroom,
  getSessions: getEmbedSessions,
  getCustomerSessions: getCustomerEmbedSessions
} as const
